import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@domcrm/embed-ui';

import { bookFitnessSession, cancelFitnessSession, listFitnessSessions } from '../widget-api/fitness';
import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';
import { PATH } from '../widget-lib/path-routes';

type Sess = { id: number; program_name: string; starts_at: string; available_spots: number };

function formatSessionWhen(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('ru-RU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return iso;
  }
}

function IconDumbbell() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M14.4 14.4 9.6 9.6" />
      <path d="M18.657 21.485a2 2 0 1 1-2.829-2.828l-1.767 1.768a2 2 0 1 1-2.829-2.829l6.364-6.364a2 2 0 1 1 2.829 2.829l-1.768 1.767" />
      <path d="m21.5 21.5-1.4-1.4" />
      <path d="M3.515 5.515a2 2 0 1 1 2.828-2.828l1.768 1.768a2 2 0 1 1 2.829 2.829L5.576 14.246a2 2 0 1 1-2.829-2.829l1.768-1.767" />
      <path d="M2.5 2.5 3.9 3.9" />
    </svg>
  );
}

/**
 * Групповые fitness-слоты организации: список, бронь и отмена при авторизации.
 * Маршрут: `PATH.fitness`.
 */
export default function FitnessCorePage() {
  const suffix = useQuerySuffix();
  const { organizationId, accessToken, me, meLoading, widgetContour } = useEmbedBootstrap();
  const [rows, setRows] = useState<Sess[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  const org = organizationId ?? 1;
  const canAct = Boolean(accessToken && me);
  const isMobile = widgetContour === 'mobile';

  const load = useCallback(async () => {
    if (!org) return;
    setErr(null);
    try {
      const data = (await listFitnessSessions(org, accessToken)) as Sess[];
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Ошибка загрузки');
      setRows([]);
    }
  }, [org, accessToken]);

  useEffect(() => {
    if (!meLoading) void load();
  }, [load, meLoading]);

  if (meLoading) {
    return (
      <div className="w-full min-w-0 space-y-3 px-1 py-2">
        <div className="h-6 w-48 animate-pulse rounded-lg bg-[var(--bg-soft)]" />
        <div className="h-24 animate-pulse rounded-2xl bg-[var(--bg-soft)]" />
        <div className="h-24 animate-pulse rounded-2xl bg-[var(--bg-soft)]" />
      </div>
    );
  }

  if (err) {
    return (
      <div className="w-full min-w-0 space-y-3 rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-4 shadow-[var(--shadow-card)]">
        <h1 className="text-lg font-semibold tracking-tight text-[var(--text)]">Групповой фитнес</h1>
        <p className="text-sm text-[var(--error-text)]">{err}</p>
        <Button type="button" onClick={() => void load()}>
          Повторить
        </Button>
        <p>
          <Link className="text-sm text-[var(--accent)] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]" to={`${PATH.account}${suffix}`}>
            ← В личный кабинет
          </Link>
        </p>
      </div>
    );
  }

  if (!rows?.length) {
    return (
      <div className="w-full min-w-0 space-y-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-[var(--text)]">Групповой фитнес</h1>
          <p className="mt-1 text-sm text-[var(--text-muted)]">
            Запись на групповые занятия. Когда появятся слоты в расписании студии, они отобразятся здесь.
          </p>
        </div>
        <div className="rounded-2xl border border-dashed border-[var(--border)] bg-[var(--bg-soft)] px-4 py-8 text-center shadow-sm">
          <p className="text-sm text-[var(--text-muted)]">Сейчас нет доступных слотов.</p>
        </div>
        <Link
          className="inline-flex text-sm font-medium text-[var(--accent)] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
          to={`${PATH.account}${suffix}`}
        >
          ← В личный кабинет
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-[var(--text)]">Групповой фитнес</h1>
          <p className="mt-1 text-sm text-[var(--text-muted)]">Выберите слот и забронируйте место.</p>
        </div>
        <Link
          className="text-sm font-medium text-[var(--accent)] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
          to={`${PATH.account}${suffix}`}
        >
          Кабинет
        </Link>
      </div>

      <ul className="flex flex-col gap-3">
        {rows.map((s) => (
          <li
            key={s.id}
            className="overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] shadow-[var(--shadow-card)]"
          >
            <div className="flex gap-3 border-b border-[var(--border)] bg-[var(--bg-soft)] px-4 py-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--bg-elevated)] text-[var(--accent)]">
                <IconDumbbell />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-sm font-semibold leading-snug text-[var(--text)]">{s.program_name}</h2>
                <p className="mt-0.5 text-xs text-[var(--text-muted)]">{formatSessionWhen(s.starts_at)}</p>
                <p className="mt-1 text-xs font-medium text-[var(--text)]">
                  Свободно мест: <span className="tabular-nums">{s.available_spots}</span>
                </p>
              </div>
            </div>
            {canAct ? (
              <div className="flex flex-wrap gap-2 px-4 py-3">
                <Button
                  type="button"
                  className={isMobile ? 'min-h-10 flex-1 sm:flex-none' : undefined}
                  disabled={busy != null}
                  onClick={async () => {
                    setBusy(s.id);
                    try {
                      await bookFitnessSession(s.id, org, accessToken);
                      await load();
                    } finally {
                      setBusy(null);
                    }
                  }}
                >
                  Забронировать
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  className={isMobile ? 'min-h-10 flex-1 sm:flex-none' : undefined}
                  disabled={busy != null}
                  onClick={async () => {
                    setBusy(s.id);
                    try {
                      await cancelFitnessSession(s.id, org, accessToken);
                      await load();
                    } finally {
                      setBusy(null);
                    }
                  }}
                >
                  Отменить бронь
                </Button>
              </div>
            ) : (
              <div className="px-4 py-3">
                <p className="text-xs text-[var(--text-muted)]">
                  <Link className="font-medium text-[var(--accent)] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]" to={`${PATH.signIn}${suffix}`}>
                    Войдите
                  </Link>
                  , чтобы бронировать слоты.
                </p>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
